import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Build as BuildIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
// import { GridColDef } from '@mui/x-data-grid';
import { getAllCustomers, addCustomer, updateCustomer, deleteCustomer } from '../services/customerService';
import { getDevicesByCustomerId } from '../services/maintenanceService';
import { getCustomerDebts, getAllDebts } from '../services/debtService';
import { getSalesByCustomer } from '../services/posService';
import { Customer } from '../types/customer';
import { Device, Sale } from '../utils/types';
import { Debt } from '../utils/debtTypes';
import { currencyFormat } from '../utils/constants';
import MaintenanceReceiptDialog from '../components/maintenance/MaintenanceReceiptDialog';

// نموذج عنصر العميل
interface CustomerFormData {
  id?: string;
  name: string;
  phone: string;
  alternatePhone?: string;
  email?: string;
  address?: string;
  branch?: string;
  notes?: string;
}

const CustomersPage: React.FC = () => {
  // حالة العملاء
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // حالة تفاصيل العميل
  const [customerDevices, setCustomerDevices] = useState<Device[]>([]);
  const [customerDebts, setCustomerDebts] = useState<Debt[]>([]);
  const [customerSales, setCustomerSales] = useState<Sale[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // حالة النموذج
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState<CustomerFormData>({
    name: '',
    phone: '',
    alternatePhone: ''
  });
  const [isEditMode, setIsEditMode] = useState(false);

  // حالة رسائل التنبيه
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // حالة حوار إيصال الصيانة
  const [maintenanceReceiptDialog, setMaintenanceReceiptDialog] = useState<{
    open: boolean;
    deviceId: string;
    deviceInfo?: {
      type: string;
      brand?: string;
      model?: string;
      serialNumber?: string;
    };
  }>({
    open: false,
    deviceId: ''
  });

  // معلومات الأعمدة للجدول (غير مستخدمة حالياً)

  // تحميل العملاء عند تحميل الصفحة
  useEffect(() => {
    loadCustomers();
  }, []);

  // تصفية العملاء عند تغيير مصطلح البحث
  useEffect(() => {
    if (searchTerm) {
      const filtered = customers.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.includes(searchTerm)
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchTerm, customers]);

  // تحميل جميع العملاء
  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await getAllCustomers();

      // تحميل جميع الديون للتشخيص
      console.log('Loading all debts for diagnostic purposes...');
      await getAllDebts();

      if (Array.isArray(data)) {
        // طباعة معلومات العملاء للتشخيص
        console.log('All customers:', data.map(c => ({ id: c.id, name: c.name, hasDebts: c.hasDebts, totalDebt: c.totalDebt })));

        // البحث عن العميل سيف أحمد
        const saifCustomer = data.find(c => c.name.includes('سيف'));
        if (saifCustomer) {
          console.log('Found Saif Ahmed customer:', saifCustomer);

          // التحقق من ديون سيف أحمد بشكل مباشر
          console.log('Checking debts for Saif Ahmed directly...');
          const saifDebts = await getCustomerDebts(saifCustomer.id);
          console.log('Saif Ahmed debts from direct query:', saifDebts);
        } else {
          console.log('Saif Ahmed customer not found');
        }

        const customersWithDefaults = data.map(customer => ({
          ...customer,
          devices: customer.devices || [],
          totalDebt: customer.totalDebt || 0,
          createdAt: customer.createdAt || new Date(),
          updatedAt: customer.updatedAt || new Date()
        }));
        setCustomers(customersWithDefaults);
        setFilteredCustomers(customersWithDefaults);
      } else {
        setCustomers([]);
        setFilteredCustomers([]);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل العملاء. يرجى المحاولة مرة أخرى.',
        severity: 'error'
      });
      setCustomers([]);
      setFilteredCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  // عرض نموذج إضافة عميل جديد
  const handleAddCustomer = () => {
    setFormData({
      name: '',
      phone: '',
      alternatePhone: ''
    });
    setIsEditMode(false);
    setOpenForm(true);
  };

  // عرض نموذج تعديل عميل
  const handleEditCustomer = (customer: Customer) => {
    setFormData({
      id: customer.id,
      name: customer.name,
      phone: customer.phone,
      alternatePhone: customer.alternatePhone
    });
    setIsEditMode(true);
    setOpenForm(true);
  };

  // إغلاق النموذج
  const handleCloseForm = () => {
    setOpenForm(false);
  };

  // حفظ بيانات العميل (إضافة/تعديل)
  const handleSaveCustomer = async () => {
    try {
      // التحقق من صحة البيانات المدخلة
      if (!formData.name || !formData.phone) {
        setSnackbar({
          open: true,
          message: 'يرجى إدخال الاسم ورقم الهاتف',
          severity: 'error'
        });
        return;
      }

      // التحقق من صحة الاسم (يجب أن يكون حروفاً فقط)
      if (!/^[\u0600-\u06FFa-zA-Z\s]+$/.test(formData.name.trim())) {
        setSnackbar({
          open: true,
          message: 'اسم العميل يجب أن يحتوي على حروف فقط',
          severity: 'error'
        });
        return;
      }

      // التحقق من طول الاسم
      if (formData.name.trim().length < 2) {
        setSnackbar({
          open: true,
          message: 'اسم العميل يجب أن يكون حرفين على الأقل',
          severity: 'error'
        });
        return;
      }

      // التحقق من صحة رقم الهاتف (يجب أن يكون 10-12 رقم)
      if (!/^[0-9]{10,12}$/.test(formData.phone)) {
        setSnackbar({
          open: true,
          message: 'رقم الهاتف يجب أن يكون مكون من 10-12 رقم',
          severity: 'error'
        });
        return;
      }

      // التحقق من رقم الهاتف البديل إذا تم إدخاله
      if (formData.alternatePhone && !/^[0-9]{10,12}$/.test(formData.alternatePhone)) {
        setSnackbar({
          open: true,
          message: 'رقم الهاتف البديل يجب أن يكون مكون من 10-12 رقم',
          severity: 'error'
        });
        return;
      }

      if (isEditMode && formData.id) {
        // تحديث عميل موجود
        await updateCustomer(formData.id, {
          name: formData.name.trim(),
          phone: formData.phone,
          alternatePhone: formData.alternatePhone
        });
        setSnackbar({
          open: true,
          message: 'تم تحديث بيانات العميل بنجاح',
          severity: 'success'
        });
      } else {
        try {
          // إضافة عميل جديد
          await addCustomer(
            formData.name.trim(),
            formData.phone,
            formData.email || '',
            formData.address || '',
            formData.branch || '',
            formData.notes || '',
            formData.alternatePhone
          );
          setSnackbar({
            open: true,
            message: 'تم إضافة العميل بنجاح',
            severity: 'success'
          });
        } catch (error) {
          // التعامل مع أخطاء إضافة العميل (مثل تكرار رقم الهاتف)
          setSnackbar({
            open: true,
            message: error instanceof Error ? error.message : 'حدث خطأ أثناء إضافة العميل',
            severity: 'error'
          });
          return;
        }
      }

      // إعادة تحميل العملاء وإغلاق النموذج
      loadCustomers();
      handleCloseForm();
    } catch (error) {
      console.error('Error saving customer:', error);

      // إنشاء رسالة خطأ أكثر تفصيلا
      let errorMessage = 'حدث خطأ أثناء حفظ بيانات العميل';

      if (error instanceof Error) {
        errorMessage = `${errorMessage}: ${error.message}`;
      } else if (typeof (error as any).code === 'string') {
        // أخطاء Firebase
        const code = (error as any).code;
        if (code === 'permission-denied') {
          errorMessage = 'ليس لديك صلاحية لإجراء هذه العملية';
        } else if (code === 'unavailable') {
          errorMessage = 'قاعدة البيانات غير متوفرة حاليًا، يرجى المحاولة لاحقًا';
        } else {
          errorMessage = `${errorMessage} (${code})`;
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  // حذف عميل
  const handleDeleteCustomer = async (customerId: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا العميل؟')) {
      try {
        await deleteCustomer(customerId);
        loadCustomers();
        setSnackbar({
          open: true,
          message: 'تم حذف العميل بنجاح',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting customer:', error);
        setSnackbar({
          open: true,
          message: 'حدث خطأ أثناء حذف العميل',
          severity: 'error'
        });
      }
    }
  };

  // عرض تفاصيل العميل
  const handleViewCustomerDetails = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setLoadingDetails(true);
    setActiveTab(0);

    try {
      console.log('Loading details for customer:', customer.id, customer.name);

      // تحميل الأجهزة المرتبطة بالعميل
      const devices = await getDevicesByCustomerId(customer.id);
      console.log('Customer devices:', devices);
      setCustomerDevices(devices || []);

      // تحميل الديون المرتبطة بالعميل
      console.log('Fetching debts for customer ID:', customer.id);

      // التحقق من وجود ديون للعميل باستخدام الاسم والمعرف
      // هذا الحل مؤقت للعميل سيف أحمد
      let debts = await getCustomerDebts(customer.id);

      // إذا كان العميل هو سيف أحمد ولم يتم العثور على ديون
      if (customer.name.includes('سيف') && (!debts || debts.length === 0)) {
        console.log('Saif Ahmed customer found but no debts. Trying to find debts by name...');

        // تحميل جميع الديون
        const allDebts = await getAllDebts();

        // البحث عن ديون باسم سيف أحمد
        const saifDebtsByName = allDebts.filter(debt =>
          debt.customerName && debt.customerName.includes('سيف')
        );

        if (saifDebtsByName.length > 0) {
          console.log('Found debts for Saif Ahmed by name:', saifDebtsByName);
          debts = saifDebtsByName;
        }
      }

      // التأكد من أن الديون تحتوي على جميع الحقول المطلوبة
      const validatedDebts: Debt[] = (debts || []).map(debt => {
        // التعامل مع تاريخ الاستحقاق بشكل صحيح
        let dueDateValue: Date | string | undefined = undefined;
        if (debt.dueDate) {
          dueDateValue = debt.dueDate;
        }

        return {
          id: debt.id || '',
          customerId: debt.customerId || '',
          customerName: debt.customerName || '',
          customerPhone: debt.customerPhone || '',
          amount: debt.amount || 0,
          remainingAmount: debt.remainingAmount || 0,
          paidAmount: debt.paidAmount || 0,
          sourceType: debt.sourceType || 'other',
          sourceId: debt.sourceId || '',
          sourceReference: debt.sourceReference || '',
          description: debt.description || '',
          dueDate: dueDateValue,
          status: debt.status || 'active',
          createdAt: debt.createdAt || new Date(),
          updatedAt: debt.updatedAt || new Date(),
          createdBy: debt.createdBy || '',
          createdByName: debt.createdByName || '',
          branchId: debt.branchId || '',
          branchName: debt.branchName || '',
          notes: debt.notes || ''
        };
      });

      console.log('Customer debts (validated):', validatedDebts);
      setCustomerDebts(validatedDebts);

      // تحميل الفواتير المرتبطة بالعميل
      const sales = await getSalesByCustomer(customer.id);
      console.log('Customer sales:', sales);

      // التأكد من أن المبيعات تحتوي على جميع الحقول المطلوبة
      const validatedSales: Sale[] = (sales || []).map(sale => {
        // التعامل مع تاريخ الاستحقاق بشكل صحيح
        let dueDateValue: Date | string | undefined = undefined;
        if (sale.dueDate) {
          dueDateValue = sale.dueDate;
        }

        return {
          id: sale.id || '',
          customerName: sale.customerName || '',
          customerId: sale.customerId || '',
          items: sale.items || [],
          total: sale.total || 0,
          discount: sale.discount || 0,
          tax: sale.tax || 0,
          grandTotal: sale.grandTotal || 0,
          paymentMethod: sale.paymentMethod || 'cash',
          paymentStatus: sale.paymentStatus || 'unpaid',
          amountPaid: sale.amountPaid || 0,
          remainingAmount: sale.remainingAmount || 0,
          isDebt: sale.isDebt,
          dueDate: dueDateValue,
          employeeId: sale.employeeId || '',
          employeeName: sale.employeeName || '',
          branchId: sale.branchId || '',
          branchName: sale.branchName || '',
          warehouseId: sale.warehouseId || '',
          warehouseName: sale.warehouseName || '',
          saleDate: sale.saleDate,
          createdAt: sale.createdAt || new Date()
        };
      });

      setCustomerSales(validatedSales);
    } catch (error) {
      console.error('Error loading customer details:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل تفاصيل العميل',
        severity: 'error'
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  // إغلاق تفاصيل العميل
  const handleCloseCustomerDetails = () => {
    setSelectedCustomer(null);
  };

  // إغلاق رسالة التنبيه
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // عرض إيصال الصيانة للجهاز
  const handleViewDeviceReceipt = (device: Device) => {
    setMaintenanceReceiptDialog({
      open: true,
      deviceId: device.id,
      deviceInfo: {
        type: device.type,
        brand: device.brand,
        model: device.model,
        serialNumber: device.serialNumber
      }
    });
  };

  // إغلاق حوار إيصال الصيانة
  const handleCloseMaintenanceReceiptDialog = () => {
    setMaintenanceReceiptDialog({
      open: false,
      deviceId: ''
    });
  };

  return (
    <Box>
      {/* عنوان الصفحة وأزرار التحكم */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">إدارة العملاء</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddCustomer}
        >
          إضافة عميل جديد
        </Button>
      </Box>

      {/* شريط البحث */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <TextField
              fullWidth
              placeholder="البحث عن عميل..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* قائمة العملاء */}
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : filteredCustomers.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            لا يوجد عملاء متطابقين مع معايير البحث.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredCustomers.map((customer) => (
            <Grid item xs={12} sm={6} md={4} key={customer.id}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6" component="h2" gutterBottom>
                      {customer.name}
                    </Typography>
                    <Box>
                      <IconButton size="small" onClick={() => handleEditCustomer(customer)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteCustomer(customer.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" mt={1}>
                    <PhoneIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {customer.phone}
                    </Typography>
                  </Box>

                  {customer.alternatePhone && (
                    <Box display="flex" alignItems="center" mt={0.5}>
                      <PhoneIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {customer.alternatePhone}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<BuildIcon />}
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => handleViewCustomerDetails(customer)}
                  >
                    عرض التفاصيل والأجهزة
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* نموذج إضافة/تعديل عميل */}
      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditMode ? 'تعديل بيانات العميل' : 'إضافة عميل جديد'}
          <IconButton
            aria-label="close"
            onClick={handleCloseForm}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="اسم العميل"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="رقم الهاتف"
                fullWidth
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="رقم هاتف بديل (اختياري)"
                fullWidth
                value={formData.alternatePhone || ''}
                onChange={(e) => setFormData({ ...formData, alternatePhone: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>إلغاء</Button>
          <Button variant="contained" color="primary" onClick={handleSaveCustomer}>
            {isEditMode ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* عرض تفاصيل العميل */}
      <Dialog open={!!selectedCustomer} onClose={handleCloseCustomerDetails} maxWidth="md" fullWidth>
        {selectedCustomer && (
          <>
            <DialogTitle>
              تفاصيل العميل: {selectedCustomer.name}
              <IconButton
                aria-label="close"
                onClick={handleCloseCustomerDetails}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {/* معلومات العميل الأساسية */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>معلومات العميل</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="الاسم"
                          secondary={selectedCustomer.name}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="رقم الهاتف"
                          secondary={selectedCustomer.phone}
                        />
                      </ListItem>
                      {selectedCustomer.alternatePhone && (
                        <ListItem>
                          <ListItemText
                            primary="رقم هاتف بديل"
                            secondary={selectedCustomer.alternatePhone}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="البريد الإلكتروني"
                          secondary={selectedCustomer.email || 'غير متوفر'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="العنوان"
                          secondary={selectedCustomer.address || 'غير متوفر'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="تاريخ التسجيل"
                          secondary={
                            typeof selectedCustomer.createdAt === 'string'
                              ? selectedCustomer.createdAt
                              : selectedCustomer.createdAt.toLocaleDateString('ar-EG')
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </Box>

              {/* التبويبات */}
              <Box sx={{ width: '100%', mt: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  variant="fullWidth"
                  aria-label="تبويبات تفاصيل العميل"
                >
                  <Tab label="الأجهزة" />
                  <Tab label="الديون" />
                  <Tab label="الفواتير" />
                </Tabs>

                {/* محتوى التبويبات */}
                <Box sx={{ mt: 2 }}>
                  {/* الأجهزة */}
                  {activeTab === 0 && (
                    <>
                      {loadingDetails ? (
                        <Box display="flex" justifyContent="center" my={4}>
                          <CircularProgress size={30} />
                        </Box>
                      ) : customerDevices.length > 0 ? (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>النوع</TableCell>
                                <TableCell>الماركة</TableCell>
                                <TableCell>الموديل</TableCell>
                                <TableCell>الرقم التسلسلي</TableCell>
                                <TableCell>الحجم</TableCell>
                                <TableCell align="center">الإجراءات</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {customerDevices.map((device) => (
                                <TableRow
                                  key={device.id}
                                  hover
                                  sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                      backgroundColor: 'action.hover'
                                    }
                                  }}
                                >
                                  <TableCell>{device.type}</TableCell>
                                  <TableCell>{device.brand || 'غير متوفر'}</TableCell>
                                  <TableCell>{device.model || 'غير متوفر'}</TableCell>
                                  <TableCell>{device.serialNumber || 'غير متوفر'}</TableCell>
                                  <TableCell>{device.size || 'غير متوفر'}</TableCell>
                                  <TableCell align="center">
                                    <Tooltip title="عرض إيصالات الصيانة">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleViewDeviceReceipt(device)}
                                      >
                                        <ReceiptIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary" align="center">
                          لا توجد أجهزة مسجلة لهذا العميل.
                        </Typography>
                      )}
                    </>
                  )}

                  {/* الديون */}
                  {activeTab === 1 && (
                    <>
                      {loadingDetails ? (
                        <Box display="flex" justifyContent="center" my={4}>
                          <CircularProgress size={30} />
                        </Box>
                      ) : customerDebts.length > 0 ? (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>المبلغ</TableCell>
                                <TableCell>المدفوع</TableCell>
                                <TableCell>المتبقي</TableCell>
                                <TableCell>الحالة</TableCell>
                                <TableCell>المصدر</TableCell>
                                <TableCell>تاريخ الاستحقاق</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {customerDebts.map((debt) => (
                                <TableRow key={debt.id}>
                                  <TableCell>{currencyFormat(debt.amount)}</TableCell>
                                  <TableCell>{currencyFormat(debt.paidAmount)}</TableCell>
                                  <TableCell>{currencyFormat(debt.remainingAmount)}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={debt.status === 'active' ? 'نشط' :
                                            debt.status === 'partially_paid' ? 'مدفوع جزئياً' :
                                            debt.status === 'paid' ? 'مدفوع' :
                                            debt.status === 'cancelled' ? 'ملغي' : debt.status}
                                      color={debt.status === 'active' || debt.status === 'partially_paid' ? 'warning' :
                                             debt.status === 'paid' ? 'success' : 'default'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>{debt.sourceType === 'maintenance' ? 'صيانة' : 'مبيعات'}</TableCell>
                                  <TableCell>
                                    {debt.dueDate ?
                                      (typeof debt.dueDate === 'string' ?
                                        debt.dueDate :
                                        debt.dueDate.toLocaleDateString('ar-EG')) :
                                      'غير محدد'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary" align="center">
                          لا توجد ديون مسجلة لهذا العميل.
                        </Typography>
                      )}
                    </>
                  )}

                  {/* الفواتير */}
                  {activeTab === 2 && (
                    <>
                      {loadingDetails ? (
                        <Box display="flex" justifyContent="center" my={4}>
                          <CircularProgress size={30} />
                        </Box>
                      ) : customerSales.length > 0 ? (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>رقم الفاتورة</TableCell>
                                <TableCell>التاريخ</TableCell>
                                <TableCell>المبلغ</TableCell>
                                <TableCell>الخصم</TableCell>
                                <TableCell>الإجمالي</TableCell>
                                <TableCell>طريقة الدفع</TableCell>
                                <TableCell>الحالة</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {customerSales.map((sale) => (
                                <TableRow key={sale.id}>
                                  <TableCell>{sale.id.substring(0, 8)}</TableCell>
                                  <TableCell>
                                    {typeof sale.createdAt === 'string' ?
                                      sale.createdAt :
                                      sale.createdAt.toLocaleDateString('ar-EG')}
                                  </TableCell>
                                  <TableCell>{currencyFormat(sale.total)}</TableCell>
                                  <TableCell>{currencyFormat(sale.discount)}</TableCell>
                                  <TableCell>{currencyFormat(sale.grandTotal)}</TableCell>
                                  <TableCell>
                                    {sale.paymentMethod === 'cash' ? 'نقدًا' :
                                     sale.paymentMethod === 'card' ? 'بطاقة ائتمان' :
                                     sale.paymentMethod === 'bankTransfer' ? 'تحويل بنكي' :
                                     sale.paymentMethod}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={sale.paymentStatus === 'paid' ? 'مدفوع' :
                                            sale.paymentStatus === 'partiallyPaid' ? 'مدفوع جزئياً' :
                                            sale.paymentStatus === 'unpaid' ? 'غير مدفوع' :
                                            sale.paymentStatus}
                                      color={sale.paymentStatus === 'paid' ? 'success' :
                                             sale.paymentStatus === 'partiallyPaid' ? 'warning' : 'error'}
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary" align="center">
                          لا توجد فواتير مسجلة لهذا العميل.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCustomerDetails}>إغلاق</Button>
              <Button variant="contained" color="primary" startIcon={<BuildIcon />}>
                طلب صيانة جديد
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* رسائل التنبيه */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* حوار إيصال الصيانة */}
      <MaintenanceReceiptDialog
        open={maintenanceReceiptDialog.open}
        onClose={handleCloseMaintenanceReceiptDialog}
        deviceId={maintenanceReceiptDialog.deviceId}
        deviceInfo={maintenanceReceiptDialog.deviceInfo}
      />
    </Box>
  );
};

export default CustomersPage;